import { useEffect, useState } from "react";
import { X, Loader2, Calendar, Gauge, Wallet, Fuel, Zap, Wrench, ShieldAlert, Receipt, FileCheck2, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const KIND_META = {
  fuel:            { icon: Fuel,        label: "Refuel",           accent: "#FF5A1F" },
  charging:        { icon: Zap,         label: "EV Charging",      accent: "#22D3EE" },
  routine:         { icon: Wrench,      label: "Routine Service",  accent: "#FBBF24" },
  repair:          { icon: ShieldAlert, label: "Repair",           accent: "#EF4444" },
  bill:            { icon: Receipt,     label: "Service/Repair Bill", accent: "#F472B6" },
  document:        { icon: FileCheck2,  label: "Document",         accent: "#34D399" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso.slice(0, 10);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtMoney(v) {
  if (v == null || v === 0) return "—";
  return `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function Row({ label, value, testid }) {
  if (value == null || value === "" || value === 0) return null;
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-[var(--border-line)] last:border-0" data-testid={testid}>
      <div className="text-xs text-[var(--text-muted)] uppercase tracking-wider font-semibold">{label}</div>
      <div className="text-sm font-mono text-[var(--text-primary)] text-right break-all">{value}</div>
    </div>
  );
}

export default function TimelineEventDetail({ event, onClose }) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const meta = KIND_META[event?.kind] || KIND_META.document;
  const Icon = meta.icon;

  useEffect(() => {
    if (!event) return;
    let ignore = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/reports/timeline-event/${event.record_type}/${event.id}`);
        if (!ignore) setDetail(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [event]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!event) return null;

  const rec = detail?.record || {};
  const vehicle = detail?.vehicle || event.vehicle || {};
  const items = rec.items || [];
  const components = rec.components || rec.parts_list || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
      data-testid="timeline-detail-overlay"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-2xl max-h-[100vh] sm:max-h-[90vh] overflow-y-auto bg-[var(--bg-surface)] sm:rounded-2xl border border-[var(--border-line)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="timeline-detail-modal"
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 flex items-center gap-3 px-4 py-3 border-b border-[var(--border-line)]"
          style={{ background: `linear-gradient(90deg, ${meta.accent}22, transparent), var(--bg-surface)` }}
        >
          <div
            className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: meta.accent, color: "#000" }}
          >
            <Icon size={22} strokeWidth={2.4} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="ft-overline text-[10px]">{meta.label}</div>
            <div className="font-display text-lg font-black tracking-tight truncate" data-testid="timeline-detail-title">
              {vehicle.number || vehicle.name || "Vehicle"} — {event.title}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[var(--hover)] text-[var(--text-muted)]"
            aria-label="Close"
            data-testid="timeline-detail-close"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center text-[var(--text-muted)] gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading full record…
          </div>
        ) : (
          <div className="p-4 sm:p-6 space-y-6">
            {/* Quick facts */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <QuickStat icon={Calendar} label="Date" value={fmtDate(event.date)} />
              <QuickStat icon={Gauge} label="Odometer" value={event.odometer != null ? `${event.odometer.toLocaleString()} km` : "—"} />
              <QuickStat icon={Wallet} label="Amount" value={fmtMoney(event.amount)} />
              {event.quantity != null && (
                <QuickStat icon={event.kind === "charging" ? Zap : Fuel} label={event.kind === "charging" ? "Energy" : "Fuel"} value={`${event.quantity} ${event.quantity_unit || ""}`} />
              )}
            </div>

            {/* AI alert */}
            {(event.ai_alert || rec.wear_alert || rec.anomaly) && (
              <div
                className="flex items-start gap-3 p-3 rounded-lg border"
                style={{ background: "#FF5A1F14", borderColor: "#FF5A1F55" }}
                data-testid="timeline-detail-ai-alert"
              >
                <Sparkles size={18} className="text-[#FF5A1F] flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <div className="font-bold text-[var(--text-primary)]">RayFuel insight</div>
                  <div className="text-[var(--text-secondary)]">{event.ai_alert || rec.wear_alert || rec.anomaly}</div>
                </div>
              </div>
            )}

            {/* Vehicle */}
            <Section title="Vehicle">
              <Row label="Number" value={vehicle.number} testid="detail-vehicle-number" />
              <Row label="Name" value={vehicle.name} />
              <Row label="Fuel type" value={vehicle.fuel_type} />
              <Row label="Powertrain" value={vehicle.powertrain} />
            </Section>

            {/* Refuel */}
            {event.record_type === "refuel" && (
              <Section title="Refuel details">
                <Row label="Litres" value={rec.litres != null ? `${rec.litres} L` : null} />
                <Row label="Rate / L" value={rec.rate != null ? `₹${rec.rate}` : null} />
                <Row label="Amount" value={fmtMoney(rec.amount)} />
                <Row label="Station" value={rec.station} />
                <Row label="Notes" value={rec.notes} />
              </Section>
            )}

            {/* Charging */}
            {event.record_type === "charging" && (
              <Section title="Charging details">
                <Row label="kWh" value={rec.kwh != null ? `${rec.kwh} kWh` : null} />
                <Row label="Cost" value={fmtMoney(rec.cost ?? rec.amount)} />
                <Row label="Location" value={rec.location_type} />
                <Row label="Provider" value={rec.provider} />
                <Row label="Charger type" value={rec.charger_type} />
                <Row label="Duration" value={rec.duration_min ? `${rec.duration_min} min` : null} />
                <Row label="Notes" value={rec.notes} />
              </Section>
            )}

            {/* Service log / service request */}
            {(event.record_type === "service_log" || event.record_type === "service_request") && (
              <>
                <Section title={event.record_type === "service_request" ? "Request details" : "Service details"}>
                  <Row label="Title" value={rec.title} />
                  <Row label="Category" value={rec.category} />
                  <Row label="Kind" value={rec.kind} />
                  <Row label="Status" value={rec.status} />
                  <Row label="Workshop" value={rec.workshop} />
                  <Row label="Labour" value={fmtMoney(rec.labour)} />
                  <Row label="Parts total" value={fmtMoney(rec.parts_total)} />
                  <Row label="Total" value={fmtMoney(rec.total ?? rec.approved_amount ?? rec.quoted_amount)} />
                  <Row label="Reason" value={rec.reason} />
                  <Row label="Notes" value={rec.notes} />
                </Section>
                {components?.length > 0 && (
                  <Section title="Components / parts">
                    <ul className="text-sm space-y-1 font-mono text-[var(--text-secondary)]">
                      {components.map((c, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <span className="text-[#FF5A1F]">•</span>
                          {typeof c === "string" ? c : (c.name || c.description || JSON.stringify(c))}
                          {c.amount != null && <span className="ml-auto">{fmtMoney(c.amount)}</span>}
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}
              </>
            )}

            {/* Bill */}
            {event.record_type === "service_bill" && (
              <>
                <Section title="Bill">
                  <Row label="Vendor" value={rec.vendor} />
                  <Row label="Bill #" value={rec.bill_number} />
                  <Row label="Total" value={fmtMoney(rec.total_amount)} />
                  <Row label="Tax" value={fmtMoney(rec.tax_amount)} />
                  <Row label="Category" value={rec.category} />
                  <Row label="Date" value={rec.date} />
                </Section>
                {items?.length > 0 && (
                  <Section title="Line items">
                    <ul className="text-sm space-y-1 font-mono text-[var(--text-secondary)]">
                      {items.map((it, i) => (
                        <li key={i} className="flex items-center justify-between border-b border-[var(--border-line)] last:border-0 py-2">
                          <span>{it.name || it.description || `Item ${i + 1}`}</span>
                          <span className="text-[var(--text-primary)]">{fmtMoney(it.amount || it.price)}</span>
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}
                {(rec.receipt_url || rec.file_url || rec.image_url) && (
                  <Section title="Attachment">
                    <a
                      href={rec.receipt_url || rec.file_url || rec.image_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-[#FF5A1F] hover:underline text-sm"
                      data-testid="timeline-detail-receipt-link"
                    >
                      <Receipt size={14} /> Open uploaded receipt
                    </a>
                  </Section>
                )}
                {rec.ocr_text && (
                  <Section title="OCR extract">
                    <pre className="text-xs bg-[var(--bg-elevated)] p-3 rounded border border-[var(--border-line)] whitespace-pre-wrap font-mono text-[var(--text-secondary)] max-h-48 overflow-y-auto">
                      {rec.ocr_text}
                    </pre>
                  </Section>
                )}
              </>
            )}

            {/* Document */}
            {event.record_type === "vehicle_document" && (
              <Section title="Document">
                <Row label="Type" value={rec.doc_type} />
                <Row label="Uploaded" value={fmtDate(rec.uploaded_at)} />
                <Row label="Expires" value={rec.expires_at} />
                <Row label="Filename" value={rec.filename} />
                {(rec.file_url || rec.url) && (
                  <div className="pt-3">
                    <a href={rec.file_url || rec.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#FF5A1F] hover:underline text-sm">
                      <FileCheck2 size={14} /> Open file
                    </a>
                  </div>
                )}
              </Section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="ft-overline text-[10px] mb-2">{title}</h4>
      <div className="bg-[var(--bg-elevated)] rounded-lg border border-[var(--border-line)] px-4 py-2">
        {children}
      </div>
    </div>
  );
}

function QuickStat({ icon: Icon, label, value }) {
  return (
    <div className="bg-[var(--bg-elevated)] rounded-lg border border-[var(--border-line)] p-3">
      <div className="flex items-center gap-2 text-[var(--text-muted)] text-[10px] uppercase tracking-wider font-semibold">
        <Icon size={12} /> {label}
      </div>
      <div className="mt-1 font-mono text-sm text-[var(--text-primary)] break-words">{value}</div>
    </div>
  );
}
